import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "semantic-ui-react";
import {
    FetchTransportOrderListFilterRequest,
    GeoStatusDumpTransportOrderListFilterRequest,
    PartnerQueryObject,
    SearchData,
    SearchTermObject,
    TransportOrder
} from "../../../../generated";
import {backend} from "../../../../xconvert-backend";
import {authentication} from "../../../../authentication";
import {FilterConditions, InTimeEnum, TransportOrderFilterComponent} from "./TransportOrderFilterComponent";
import {ConfigContext} from "../../context/ConfigContext";
import {getNextStopByEta, isEtaInTime} from "./Formatters";
import {TransportOrderTable} from "./TransportOrderTable";
import {ArchiveTransportOrdersModal} from "./modals/ArchiveTransportOrdersModal";
import {RouteComponentProps} from "react-router";


export function createDefaultFilter(): FilterConditions {
    return {
        dateRangeSelection: "all",
        dateType: FetchTransportOrderListFilterRequest.DateTimeFieldEnum.CREATED,
        fromDateTime: undefined,
        inTime: InTimeEnum.All,
        partners: undefined,
        status: undefined,
        term: undefined,
        toDateTime: undefined,
        valid: undefined
    };
}

export interface TransportOrderSearchProps extends RouteComponentProps<any> {
    selectedOrderIdsChanged: (newSelectedOrderIds: string[]) => void,
    selectedOrderChanged: (newSelectedOrder: TransportOrder) => void,
    reload: (page: number, take: number) => void,
    initialPageSize: number,
    hideArchiveButton: boolean,
    foundTransportOrdersChanged: (newTransportOrders: TransportOrder[]) => void
    preSelectedTransportOrderIds?: string[]
    allowMultipleSelection?: boolean
}

export function TransportOrderSearch(props: TransportOrderSearchProps) {

    const context = React.useContext(ConfigContext);

    const [take, setTake] = useState(25);
    const [page, setPage] = useState(0);
    // Ref statt State: Selektion soll KEINEN Re-Render von TransportOrderSearch auslösen,
    // da sonst TransportOrderFilterComponent neue Props bekommt und neu laden kann.
    const selectedTransportOrderIdsRef = useRef<string[]>([]);
    const [sortBy, setSortBy] = useState('created');
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [transportOrders, setTransportOrders] = useState<TransportOrder[]>([]);
    const [transportOrderAmount, setTransportOrderAmount] = useState(0);
    const [filter, setFilter] = useState<FilterConditions>(createDefaultFilter());
    const [showArchiveTransportOrdersModal, setShowArchiveTransportOrdersModal] = useState(false);
    // IDs nur für das Modal als State – werden erst beim Öffnen des Modals gesetzt
    const [archiveTransportOrderIds, setArchiveTransportOrderIds] = useState<string[]>([]);
    const [isFetchingTours, setIsFetchingTours] = useState(true); // loader for fetching tours/table
    const [isDownloadingDump, setIsDownloadingDump] = useState(false); // loader for dump file download

    useEffect(() => {
        setTake(props.initialPageSize);
        preSelectTransportOrders();
    }, []);

    function preSelectTransportOrders() {
        if (props.preSelectedTransportOrderIds) {
            selectedTransportOrderIdsRef.current = props.preSelectedTransportOrderIds;
        }
    }

    async function fetchTransportOrders(
        pageParam: number = page,
        takeParam: number = take,
        sortByParam: string = sortBy,
        sortDirectionParam: "asc" | "desc" = sortDirection,
        filterOverride?: FilterConditions
    ) {
        const activeFilter = filterOverride ?? filter;
        setIsFetchingTours(true);
        try {
            let auth = backend.withTokenAuthHeader(authentication.token);
            let begin = activeFilter.fromDateTime;
            let dateTimeField = activeFilter.dateType;
            let end = activeFilter.toDateTime;
            let searchTerm = activeFilter.term;

            let filterPartnerIds: string[] | undefined = undefined;
            let filterPartnerNames: string | undefined = undefined;
            activeFilter.partners?.forEach((p: PartnerQueryObject) => {
                if (p.companyId) {
                    if (!filterPartnerIds) {
                        filterPartnerIds = [];
                    }
                    filterPartnerIds.push(p.companyId);
                } else if (p.name) {
                    filterPartnerNames = (filterPartnerNames || "") + (filterPartnerNames ? "," : "") + p.name;
                }
            });

            let searchTermObjects: SearchTermObject[] | undefined = undefined;
            if (activeFilter.term && activeFilter.fieldsToSearch?.fieldsToSearch && activeFilter.fieldsToSearch.fieldsToSearch.length > 0) {
                searchTermObjects = [];
                const termTrimmed = activeFilter.term?.trim();
                const termParts = termTrimmed ? termTrimmed.split(" ") : [];
                activeFilter.fieldsToSearch.fieldsToSearch.forEach((field) => {
                    if (termParts.length > 1) {
                        termParts.forEach((part) => {
                            const searchData: SearchData = {
                                value: part,
                                additionalDataKeyName: field.additionalDataKey
                            };
                            searchTermObjects!.push({
                                searchForTransportOrderPart: field.transportOrderPart,
                                searchData,
                            });
                        });
                    } else {
                        const searchData: SearchData = {
                            value: termTrimmed ?? "",
                            additionalDataKeyName: field.additionalDataKey
                        };
                        searchTermObjects!.push({
                            searchForTransportOrderPart: field.transportOrderPart,
                            searchData,
                        });
                    }
                });
            }

            let request = {} as FetchTransportOrderListFilterRequest;
            request.companyId = context.companyId;
            request.begin = begin;
            request.dateTimeField = dateTimeField;
            request.end = end;
            request.exactSearchTermMatch = false;
            request.searchTerm = searchTermObjects ? undefined : searchTerm;
            request.searchTermFilter = searchTermObjects;
            request.skip = (pageParam) * takeParam;
            request.sortBy = sortByParam;
            request.sortOrder = sortDirectionParam;
            request.take = takeParam;
            request.searchInAllCompanies = activeFilter.searchInAllCompanies ?? false;
            request.countTransportOrders = false;
            request.useFastBasicFilter = true;
            request.includeStatus = activeFilter.includeStatus;
            request.includeStopLocationStatus = activeFilter.includeStopLocationStatus;
            request.includeFreightStatus = activeFilter.includeFreightStatus;
            request.validationStatus = activeFilter.valid as FetchTransportOrderListFilterRequest.ValidationStatusEnum;
            request.hasVehicle = activeFilter.hasVehicle;
            request.partnerIds = filterPartnerIds;
            request.partnerNameRegex = filterPartnerNames;

            let response = await backend.transportOrderApi.fetchTransportOrdersWithFilter(
                request,
                auth
            );

            let orders = response.transportOrders ?? [];
            if (activeFilter.inTime === InTimeEnum.InTime || activeFilter.inTime === InTimeEnum.NotInTime) {
                orders = orders.filter((order) => {
                    const nextStop = getNextStopByEta(order);
                    if (nextStop?.eta?.eta && nextStop.planArrive) {
                        if (activeFilter.inTime === InTimeEnum.InTime && isEtaInTime(nextStop.planArrive, nextStop.eta.eta)) {
                            return true;
                        }
                        if (activeFilter.inTime === InTimeEnum.NotInTime && !isEtaInTime(nextStop.planArrive, nextStop.eta.eta)) {
                            return true;
                        }
                    }
                    return false;
                });
            }

            setTransportOrders(orders);
            try {
                let count: number;
                if (activeFilter.inTime === InTimeEnum.InTime || activeFilter.inTime === InTimeEnum.NotInTime) {
                    count = orders.length;
                } else if (activeFilter.searchInAllCompanies === true) {
                    count = orders.length;
                } else {
                    const countResponse = await backend.transportOrderApi.countTransportOrdersWithFilter(request, auth);
                    count = countResponse.count;
                }
                setTransportOrderAmount(count);
            } catch (ex) {
                setTransportOrderAmount(orders.length);
            }
            props.foundTransportOrdersChanged(orders);
        } catch (error) {
            // Optionally handle error
            console.error(error);
        } finally {
            setIsFetchingTours(false);
        }
    }

    async function downloadDumpFiles() {
        const selectedIds = selectedTransportOrderIdsRef.current;
        if (!selectedIds || selectedIds.length === 0) {
            alert("Please select at least one transport order.");
            return;
        }
        setIsDownloadingDump(true);
        try {
            const auth = backend.withTokenAuthHeader(authentication.token);

            // Genau die selektierten Aufträge über ihre _id ansprechen
            const searchTermFilter: SearchTermObject[] = selectedIds.map((id) => ({
                searchForTransportOrderPart: SearchTermObject.SearchForTransportOrderPartEnum.TransportOrderId,
                searchData: {value: id} as SearchData,
            }));

            const filter = {} as FetchTransportOrderListFilterRequest;
            filter.companyId = context.companyId;
            filter.searchTermFilter = searchTermFilter;
            filter.exactSearchTermMatch = true;
            filter.skip = 0;
            filter.take = selectedIds.length;
            filter.countTransportOrders = false;
            filter.useFastBasicFilter = true;

            const request: GeoStatusDumpTransportOrderListFilterRequest = {
                filter,
                includeLinked: false,
            };

            const response = await backend.internalApi.createDumpV2FilteredEndpoint(request, auth);

            const blob = new Blob([JSON.stringify(response, null, 2)], {type: "application/json"});
            const downloadUrl = window.URL.createObjectURL(blob);
            const tempLink = document.createElement("a");
            tempLink.href = downloadUrl;
            tempLink.setAttribute("download", `transport-order-dump-${new Date().toISOString()}.json`);
            tempLink.click();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error(error);
            alert("Could not download dump files.");
        } finally {
            setIsDownloadingDump(false);
        }
    }

    // initialFilter stabil halten – kein neues Objekt bei jedem Render
    const stableInitialFilter = useMemo(() => createDefaultFilter(), []);

    // onFilterChange stabil halten – ändert sich NICHT wenn Selektion wechselt
    const onFilterChange = useCallback((fc: FilterConditions, _) => {
        setPage(0);
        setFilter(fc);
        fetchTransportOrders(0, take, sortBy, sortDirection, fc).then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [take, sortBy, sortDirection]);

    function drawFilters() {
        return <TransportOrderFilterComponent
            onFilterChange={onFilterChange}
            history={props.history}
            initialFilter={stableInitialFilter}
            exportButtonVisible={true}
            partnerSelectionVisible={true}
            statusFilterVisible={true}
            location={props.location}
            match={props.match}
        />
    }

    function drawTable() {
        return <TransportOrderTable
            transportOrderAmount={transportOrderAmount}
            transportOrders={transportOrders}
            selectedOrderChanged={(newSelectedOrder) => {
                props.selectedOrderChanged(newSelectedOrder);
            }}
            selectedOrderIdsChanged={(newSelectedOrderIds) => {
                // Nur Ref aktualisieren – kein State-Update, kein Re-Render!
                selectedTransportOrderIdsRef.current = newSelectedOrderIds;
                props.selectedOrderIdsChanged(newSelectedOrderIds);
            }}
            reload={(pageParam, takeParam, sortByParam, sortDirectionParam: "asc" | "desc") => {
                setPage(pageParam);
                setTake(takeParam);
                if (sortByParam !== undefined) {
                    setSortBy(sortByParam);
                }
                if (sortDirectionParam !== undefined) {
                    setSortDirection(sortDirectionParam);
                }
                fetchTransportOrders(pageParam, takeParam, sortByParam, sortDirectionParam).then();
            }}
            initialPageSize={props.initialPageSize}
            preSelectedTransportOrderIds={props.preSelectedTransportOrderIds}
            allowMultipleSelection={props.allowMultipleSelection}
            loading={isFetchingTours} // use new loader for table
        />
    }

    function modals() {
        return <ArchiveTransportOrdersModal
            isOpen={showArchiveTransportOrdersModal}
            onClose={() => {
                setShowArchiveTransportOrdersModal(false);
                fetchTransportOrders();
            }}
            transportOrderIds={archiveTransportOrderIds}/>
    }

    return <>
        {drawFilters()}
        {!props.hideArchiveButton &&
            <span style={{margin: "0 0 5px 0"}}>
                <Button onClick={() => {
                    // IDs erst beim Öffnen des Modals in State schreiben
                    setArchiveTransportOrderIds(selectedTransportOrderIdsRef.current);
                    setShowArchiveTransportOrdersModal(true);
                }}>
                    Archive selected
                </Button>
                <Button
                    loading={isDownloadingDump}
                    disabled={isDownloadingDump}
                    onClick={() => downloadDumpFiles()}>
                    Download dump files
                </Button>
            </span>
        }
        {drawTable()}
        {modals()}
    </>
}